import clsx from "clsx";

import RenderIf from "common/components/RenderIf";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useWorkspaceAccounts from "common/datahooks/useWorkspaceAccounts";
import { formatTimestamp } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { ConversationParticipant } from "common/types";

import EmojiReactions from "./EmojiReactions";
import Reactions from "./Reactions";

import type { JSX } from "react";
import type { ConversationMessage } from "../../../types";

const groupColors = [
  "text-red-500",
  "text-blue-500",
  "text-purple-500",
  "text-yellow-500",
  "text-green-500",
  "text-orange-500",
  "text-magenta-500",
];

interface MessageBubbleProps {
  accountId: string;
  isMine: boolean;
  message: ConversationMessage | undefined;
  participants: ConversationParticipant[];
  setEditMessage?: () => void;
  children: JSX.Element;
}

export default function MessageBubble({
  accountId,
  isMine,
  message,
  participants,
  setEditMessage = null,
  children,
}: MessageBubbleProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { id } = useSelectedWorkspaceContext();
  const { workspaceAccounts } = useWorkspaceAccounts(id);

  const {
    created_at: createdAt,
    edited: isEdited,
    deleted: isDeleted,
    sender,
  } = message;

  const canModify = createdAt >= Date.now() - 1000 * 60 * 60 && isMine;

  const getSenderColor = (senderId: string) => {
    if (participants?.length > 2) {
      const senderIndex = participants.findIndex(
        (participant) => participant.id === senderId,
      );
      return groupColors[senderIndex % groupColors.length];
    }
    return null;
  };

  const getBackgroundColor = () => {
    if (isDeleted) {
      return "bg-red-50";
    }
    return isMine ? "bg-purple-50" : "bg-black-100";
  };

  const senderData = isMine
    ? workspaceAccounts.find(({ id }) => id === sender?.id)
    : sender;

  return (
    <div
      className={clsx([
        "flex flex-col gap-1 group",
        isMine ? "items-end rounded-br-none" : "items-start rounded-tl-none",
      ])}
    >
      <div
        className={clsx([
          "flex flex-row items-center gap-2",
          isMine ? "flex-row-reverse" : "flex-row",
        ])}
      >
        <div
          className={clsx([
            "flex flex-row gap-2",
            isMine ? "flex-row-reverse" : "flex-row",
          ])}
        >
          <div
            className={clsx([
              "flex w-fit max-w-[332px] flex-col items-start justify-center rounded-3xl p-2 md:max-w-[540px]",
              isMine ? "rounded-tr-none" : "rounded-tl-none",
              getBackgroundColor(),
            ])}
          >
            <RenderIf condition={!!createdAt}>
              <div
                className={clsx([
                  "mb-1 flex w-full flex-row items-center justify-between gap-2",
                  isMine ? "mr-auto" : "ml-auto",
                ])}
              >
                <span
                  className={clsx(
                    "text-sm-bold",
                    !isMine && getSenderColor(sender?.id),
                  )}
                >
                  {senderData?.full_name}
                </span>

                <span className="text-xs text-black-500">
                  {formatTimestamp(createdAt).toUpperCase()}
                  {isEdited ? " (Edited)" : ""}
                </span>
              </div>
            </RenderIf>

            {children}
          </div>
        </div>

        {/* Reactions with default emojis on desktop */}
        <RenderIf condition={!!message && isDesktop}>
          <EmojiReactions
            senderId={accountId}
            isMenuVisible={isMine && canModify && !isDeleted}
            message={message}
            setEditMessage={setEditMessage}
          />
        </RenderIf>
      </div>

      <div
        className={clsx([
          "flex w-full flex-row items-start justify-between gap-4",
          isMine ? "lg:justify-end" : "lg:justify-start",
        ])}
      >
        <Reactions
          accountId={accountId}
          reactions={message.reactions}
          message={message}
        />

        <RenderIf condition={!!message && !isDesktop}>
          {/* Reactions with default emojis on mobile */}
          <div className="ml-auto">
            <EmojiReactions
              senderId={accountId}
              isMenuVisible={isMine && canModify && !isDeleted}
              message={message}
              setEditMessage={setEditMessage}
            />
          </div>
        </RenderIf>
      </div>
    </div>
  );
}
